import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, History, RotateCcw, Save, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useEditor } from "./useEditorState";
import { errMsg, formatDate } from "@/lib/api";

const KIND = { manual: ["Manuale", "bg-navy-900 text-white"], "auto-ai": ["Auto · prima di AI", "bg-sky-50 text-sky-700"], auto: ["Automatica", "bg-slate-100 text-slate-600"] };

export default function VersionsSheet() {
  const ed = useEditor();
  const open = !!ed.ui.versions;
  const [list, setList] = useState([]);
  const [label, setLabel] = useState("");
  const [restoring, setRestoring] = useState(null);
  const load = useCallback(async () => { const { data } = await ed.api.get(`/projects/${ed.id}/versions`); setList(data); }, [ed.api, ed.id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const saveVersion = async () => {
    try {
      await ed.save();
      const { data } = await ed.api.post(`/projects/${ed.id}/versions`, { label: label || "Versione salvata" });
      setLabel(""); load();
      const b = data.site_build;
      if (b?.outcome === "images_blocked") toast.warning("Versione salvata, anteprima del sito NON aggiornata", { description: `«${b.site_name}»: ${b.problems} ${b.problems === 1 ? "immagine non consentita" : "immagini non consentite"} (non verificate, esterne o stock). Dettagli nella pagina del sito.` });
      else toast.success("Versione salvata", b ? { description: `Anteprima del sito «${b.site_name}» in aggiornamento (compilazione n. ${b.number})` } : undefined);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const restore = async () => {
    const v = restoring; setRestoring(null);
    try { await ed.save(); const { data } = await ed.api.post(`/projects/${ed.id}/versions/${v.id}/restore`); ed.replaceContent(data.content, { keepHistory: true }); load(); toast.success(`Ripristinata "${v.label}" (lo stato precedente è stato salvato)`); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (v) => { await ed.api.delete(`/projects/${ed.id}/versions/${v.id}`); load(); };

  return (
    <Sheet open={open} onOpenChange={(o) => ed.setUi({ versions: o })}>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="versions-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><History size={18} />Versioni</SheetTitle>
          <SheetDescription>Una versione viene salvata automaticamente prima di ogni modifica AI e di ogni ripristino.</SheetDescription>
        </SheetHeader>
        <div className="flex gap-2 mt-4">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Nome versione (es. Prima della riunione)" data-testid="version-label-input" />
          <Button onClick={saveVersion} data-testid="save-version-btn"><Save size={14} className="mr-1.5" />Salva</Button>
        </div>
        <ul className="mt-5 flex-1 overflow-auto divide-y" data-testid="versions-list">
          {list.map((v) => (
            <li key={v.id} className="py-3 flex items-start gap-3" data-testid={`version-item-${v.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900 truncate">{v.label}</p>
                <div className="mt-1 flex items-center gap-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${(KIND[v.kind] || KIND.auto)[1]}`}>{(KIND[v.kind] || KIND.auto)[0]}</span><span className="text-[11px] text-slate-500">{formatDate(v.created_at, true)}</span></div>
              </div>
              <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => window.open(`/preview/${ed.id}?version=${v.id}`, "_blank")} aria-label="Anteprima" title="Anteprima di questa versione" data-testid={`version-preview-${v.id}`}><Eye size={14} /></Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => setRestoring(v)} data-testid={`version-restore-${v.id}`}><RotateCcw size={13} className="mr-1" />Ripristina</Button>
              {v.kind === "manual" && <button onClick={() => remove(v)} className="p-2 text-slate-400 hover:text-red-600" aria-label="Elimina"><Trash2 size={14} /></button>}
            </li>
          ))}
          {!list.length && <p className="text-sm text-slate-500 py-6 text-center">Nessuna versione salvata.</p>}
        </ul>
        <ConfirmDialog open={!!restoring} onOpenChange={(o) => !o && setRestoring(null)} title={`Ripristinare "${restoring?.label}"?`}
          description="Lo stato attuale verrà salvato automaticamente come nuova versione prima del ripristino." confirmLabel="Ripristina" onConfirm={restore} />
      </SheetContent>
    </Sheet>
  );
}
